import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { LeftOutlined } from '@ant-design/icons';
import Button from 'admin/components/common/Button';
import Text from 'admin/components/common/Text';
import css from './DesignTokenControl.scss';

type Props = {
	/** Such as "Edit token" */
	title: string;
	/** What is edited, shown as its picker row */
	subject: React.ReactNode;
	help: string;
	error?: string;
	busy: boolean;
	canSave: boolean;
	/** Cancel, Esc and ‹ return to the picker the editor was opened from */
	onBack: () => void;
	onSave: () => void;
	className?: string;
	children: React.ReactNode;
};

/**
 * The editor a token or asset row opens from its picker: the picker's header and footer anatomy, the value field in
 * between. Enter saves, Esc goes back.
 */
export default function TokenEditorPanel({
	title,
	subject,
	help,
	error,
	busy,
	canSave,
	onBack,
	onSave,
	className,
	children,
}: Props) {
	const panelRef = useRef<HTMLDivElement>(null);
	const save = () => {
		if (canSave && !busy) onSave();
	};
	// Focus starts in the value field, or on the panel, so Enter and Esc reach the editor
	useEffect(() => {
		const panel = panelRef.current;
		const field = panel?.querySelector<HTMLElement>('input[type="text"], [aria-selected="true"]');
		(field ?? panel)?.focus({ preventScroll: true });
	}, []);

	return (
		<div
			ref={panelRef}
			tabIndex={-1}
			className={cn(css.panel, className)}
			onKeyDown={event => {
				if (event.key === 'Escape') {
					event.stopPropagation();
					onBack();
				}
				// Enter saves from the value field or a list option; fields with an Enter of their own, such as From
				// Link, and the footer buttons keep theirs
				const target = event.target as HTMLElement;
				const savesOnEnter =
					target === event.currentTarget ||
					target.classList.contains(css.valueField) ||
					target.getAttribute('role') === 'option';
				if (event.key === 'Enter' && savesOnEnter) {
					event.preventDefault();
					save();
				}
			}}
		>
			<header className={css.editorHeader}>
				<div className={css.editorTitle}>
					<button
						type="button"
						className={css.editorBack}
						aria-label="Back to the list"
						disabled={busy}
						onClick={onBack}
					>
						<LeftOutlined />
					</button>
					<strong>{title}</strong>
				</div>
				<div className={css.editorSubject}>{subject}</div>
			</header>
			<div className={css.editorBody} data-invalid={!!error}>
				{children}
				{error ? (
					<p className={css.error} role="alert">
						{error}
					</p>
				) : (
					<p className={css.editorHelp}>{help}</p>
				)}
			</div>
			<footer className={css.editorFooter}>
				<Button
					view="label-only"
					size="tiny"
					theme="dark"
					color="white"
					textSize={Text.size.description}
					textWeight={Text.weight.normal}
					disabled={busy}
					onClick={onBack}
				>
					Cancel
				</Button>
				<Button
					view="label-only"
					size="tiny"
					theme="dark"
					color="primary"
					textSize={Text.size.description}
					disabled={!canSave || busy}
					onClick={save}
				>
					{busy ? 'Saving…' : 'Save'}
				</Button>
			</footer>
		</div>
	);
}
